import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import { IProductionManagerSession } from "../../models/ProductionManager";

interface InProgressSessionsColumnProps {
  title: string;
  sessions: IProductionManagerSession[];
  now: DateTime;
  /** Route prefix to deep-link a row to its detail page, e.g. "triage" or "depollution". */
  routePrefix: string;
}

const formatDuration = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safe / 3600).toString().padStart(2, "0");
  const m = Math.floor((safe % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(safe % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const elapsedSeconds = (session: IProductionManagerSession, now: DateTime) => {
  const live = session.startedOn
    ? Math.max(0, Math.floor(now.diff(DateTime.fromISO(session.startedOn, { zone: "utc" }), "seconds").seconds))
    : 0;
  return session.elapsedSeconds + live;
};

export default function InProgressSessionsColumn({
  title,
  sessions,
  now,
  routePrefix,
}: InProgressSessionsColumnProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-md">
      <div className="flex items-baseline justify-between border-b border-gray-200 px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</h2>
        <span className="text-[11px] text-gray-500">{sessions.length} in progress</span>
      </div>

      {sessions.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-gray-500">None in progress.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {sessions.map((session) => (
            <li
              key={session.id}
              onClick={() => navigate(`/${routePrefix}/${session.id}`)}
              className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold tracking-wide text-gray-900">
                  {session.vrm ?? "Unknown"}
                </div>
                <div className="truncate text-xs text-gray-500">
                  {[session.combinedMake, session.combinedModel].filter(Boolean).join(" ") || session.stockNumber || "-"}
                </div>
              </div>
              <div className="font-mono text-sm tabular-nums text-primary-600">
                {formatDuration(elapsedSeconds(session, now))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
